import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { login, register } from "../api/authClient";
import { useAuth } from "../contexts/AuthContext";

export default function LoginPage({ isSignup = false }) {
    const [isLogin, setIsLogin] = useState(!isSignup);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [totpCode, setTotpCode] = useState("");
    const [requires2FA, setRequires2FA] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { checkAuth } = useAuth();
    const totpInputRef = useRef(null);

    const from = location.state?.from?.pathname || "/portfolio";

    // Password validation helper
    const validatePassword = (pwd) => {
        const errors = [];
        if (pwd.length < 8) errors.push("At least 8 characters.");
        if (!/[A-Z]/.test(pwd)) errors.push("At least one uppercase letter.");
        if (!/[a-z]/.test(pwd)) errors.push("At least one lowercase letter.");
        if (!/[0-9]/.test(pwd)) errors.push("At least one number.");
        if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pwd)) errors.push("At least one special character.");
        return errors;
    };

    // Sync state with prop when route changes
    useEffect(() => {
        setIsLogin(!isSignup);
        setError("");
        setSuccess("");
        setName("");
        setConfirmEmail("");
        setPassword("");
        setConfirmPassword("");
        setTotpCode("");
        setRequires2FA(false);
        setShowPassword(false);
        setShowConfirmPassword(false);
    }, [isSignup]);

    // Focus on TOTP input when 2FA is required
    useEffect(() => {
        if (requires2FA && totpInputRef.current) {
            totpInputRef.current.focus();
        }
    }, [requires2FA]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Validation for registration
        if (!isLogin) {
            if (email !== confirmEmail) {
                setError("Emails do not match");
                return;
            }
            if (password !== confirmPassword) {
                setError("Passwords do not match");
                return;
            }
            // Password complexity validation
            const passwordErrors = validatePassword(password);
            if (passwordErrors.length > 0) {
                setError(passwordErrors.join(" "));
                return;
            }
        }

        setLoading(true);
        try {
            if (isLogin) {
                const response = await login(email, password, totpCode || null);

                // Check if 2FA is required
                if (response.requires2FA) {
                    setRequires2FA(true);
                    setLoading(false);
                    return;
                }

                await checkAuth();
                navigate(from, { replace: true });
            } else {
                await register(name, email, password);
                setSuccess("Registration successful! Please check your email to verify your account.");
                setName("");
                setEmail("");
                setConfirmEmail("");
                setPassword("");
                setConfirmPassword("");
            }
        } catch (err) {
            const message = err.response?.data?.message || err.response?.data?.token || err.message;
            setError(message || "An error occurred. Please try again.");
            // If 2FA code was wrong, don't clear credentials
            if (!requires2FA) {
                setTotpCode("");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBackTo2FA = () => {
        setRequires2FA(false);
        setTotpCode("");
        setError("");
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
            <div className="w-full max-w-md">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold">O</span>
                            </div>
                            <span className="text-xl font-semibold text-gray-900">OmniWorth</span>
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isLogin ? "Welcome back" : "Create an account"}
                        </h1>
                        <p className="text-gray-500 mt-2">
                            {isLogin
                                ? "Enter your credentials to access your account"
                                : "Get started with your free account"}
                        </p>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-danger-500">{error}</p>
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                            <p className="text-sm text-accent-600">{success}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* 2FA Code Entry */}
                        {requires2FA ? (
                            <>
                                <div className="text-center mb-4">
                                    <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-700 text-sm">
                                        Enter the 6-digit code from your authenticator app
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Authentication Code
                                    </label>
                                    <input
                                        ref={totpInputRef}
                                        type="text"
                                        value={totpCode}
                                        onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                                        required
                                        maxLength={9}
                                        className="input text-center text-2xl tracking-widest font-mono"
                                        placeholder="000000"
                                        autoComplete="one-time-code"
                                    />
                                    <p className="text-xs text-gray-500 mt-2 text-center">
                                        You can also use a backup code (format: XXXX-XXXX)
                                    </p>
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading || totpCode.length < 6}
                                    className="btn-primary w-full"
                                >
                                    {loading ? "Verifying..." : "Verify"}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBackTo2FA}
                                    className="w-full text-sm text-gray-500 hover:text-gray-900 transition-colors"
                                >
                                    Use a different account
                                </button>
                            </>
                        ) : (
                            <>
                                {!isLogin && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            className="input"
                                            placeholder="Your name"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="input"
                                        placeholder="you@example.com"
                                    />
                                </div>
                                {!isLogin && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirm Email
                                        </label>
                                        <input
                                            type="email"
                                            value={confirmEmail}
                                            onChange={(e) => setConfirmEmail(e.target.value)}
                                            required
                                            className="input"
                                            placeholder="Confirm your email"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={isLogin ? undefined : 8}
                                            className="input pr-10"
                                            placeholder={isLogin ? "Enter your password" : "Create a strong password"}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    {!isLogin && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Must be 8+ characters with uppercase, lowercase, number, and special character
                                        </p>
                                    )}
                                    {isLogin && (
                                        <div className="text-right mt-2">
                                            <Link
                                                to="/forgot-password"
                                                className="text-sm text-gray-500 hover:text-accent-600 transition-colors"
                                            >
                                                Forgot password?
                                            </Link>
                                        </div>
                                    )}
                                </div>
                                {!isLogin && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirm Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                className="input pr-10"
                                                placeholder="Confirm your password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                                            >
                                                {showConfirmPassword ? (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {!isLogin && (
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-xs text-gray-500 text-center">
                                            OmniWorth connects to 12,000+ US financial institutions including Chase, Bank of America, Wells Fargo, Capital One, and most major banks.{" "}
                                            <a
                                                href="https://plaid.com/works-with/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-accent-600 hover:underline"
                                            >
                                                Check if your bank is supported
                                            </a>
                                        </p>
                                    </div>
                                )}
                                {!isLogin && (
                                    <p className="text-xs text-gray-500 text-center">
                                        By creating an account, you agree to our{" "}
                                        <Link to="/terms" className="text-accent-600 hover:underline">
                                            Terms of Service
                                        </Link>{" "}
                                        and{" "}
                                        <Link to="/privacy" className="text-accent-600 hover:underline">
                                            Privacy Policy
                                        </Link>
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full"
                                >
                                    {loading ? "Please wait..." : isLogin ? "Sign in" : "Create account"}
                                </button>
                            </>
                        )}
                    </form>

                    {!requires2FA && !isLogin && (
                        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-2">
                            <svg className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <p className="text-xs text-gray-500">
                                Your bank credentials are handled by Plaid. We never see them. OmniWorth has read-only access and cannot move money.
                            </p>
                        </div>
                    )}

                    {!requires2FA && (
                        <div className="mt-6 text-center">
                            <p className="text-gray-700">
                                {isLogin ? "Don't have an account? " : "Already have an account? "}
                                <Link
                                    to={isLogin ? "/signup" : "/login"}
                                    className="text-accent-600 hover:text-accent-700 font-medium transition-colors"
                                >
                                    {isLogin ? "Sign up" : "Sign in"}
                                </Link>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
